import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import {
  Button,
  EmptyState,
  ErrorBanner,
  FileTree,
  FrmIcon,
  MetricStrip,
  PageHeader,
  Pill,
  SearchInput,
  SegmentedControl,
  Select,
  Skeleton,
  StatusPill,
  Surface,
  WorkbenchPanel,
  type FileTreeNode,
} from '@frm/ui'

import { fetchProjects, projectPath, type ProjectDto, type ProjectStatus } from '../api/projects'
import { fetchSessions, type OwnerSessionDto } from '../api/sessions'
import { formatInteger, MONO_NUMERIC_STYLE } from '../shared/format'

type ViewMode = 'cards' | 'tree'
type SortMode = 'updated' | 'ahead'
type FocusMode = 'all' | 'sessions' | 'ahead' | 'tasks'

const PAGE_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
  minWidth: 0,
}

const PANEL_STYLE: CSSProperties = {
  boxShadow: 'var(--frm-workbench-shadow-panel)',
}

const STACK_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
}

const TOOLBAR_STYLE: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'nowrap',
  gap: 10,
  justifyContent: 'space-between',
  minWidth: 0,
  overflowX: 'auto',
  paddingBottom: 2,
}

const CARD_GRID_STYLE: CSSProperties = {
  display: 'grid',
  gap: 14,
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
}

const MUTED_STYLE: CSSProperties = {
  color: 'var(--frm-text-muted)',
}

const STATUS_ORDER: ProjectStatus[] = ['active', 'planned', 'closed']

const STATUS_META: Record<ProjectStatus, {
  label: string
  groupLabel: string
  note: string
  statusKind: 'ok' | 'warning' | 'synced'
  tone: 'info' | 'warning' | 'neutral'
  dot: string
}> = {
  active: {
    label: 'active',
    groupLabel: 'Активные',
    note: 'идёт спринт · приходят коммиты',
    statusKind: 'ok',
    tone: 'info',
    dot: 'var(--frm-accent)',
  },
  planned: {
    label: 'planned',
    groupLabel: 'В планировании',
    note: 'концепция черновая · спринт не открыт',
    statusKind: 'warning',
    tone: 'warning',
    dot: 'var(--frm-accent-2)',
  },
  closed: {
    label: 'closed',
    groupLabel: 'Закрытые',
    note: 'свёрнуты или заархивированы',
    statusKind: 'synced',
    tone: 'neutral',
    dot: 'var(--frm-text-muted)',
  },
}

const PROJECT_GRADIENTS = [
  ['#38BDF8', '#6366F1'],
  ['#6366F1', '#8B5CF6'],
  ['#34D399', '#0EA5E9'],
  ['#0EA5E9', '#0369A1'],
  ['#FBBF24', '#F59E0B'],
  ['#64748B', '#475569'],
]

function projectGradient(project: ProjectDto): string {
  const hash = project.id.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)
  const pair = PROJECT_GRADIENTS[hash % PROJECT_GRADIENTS.length]
  const from = pair?.[0] ?? '#38BDF8'
  const to = pair?.[1] ?? '#6366F1'
  return `linear-gradient(135deg, ${from}, ${to})`
}

function projectAccent(project: ProjectDto): string {
  const gradient = projectGradient(project)
  return gradient.match(/#(?:[0-9a-f]{6})/i)?.[0] ?? '#38BDF8'
}

function aheadCount(project: ProjectDto): number {
  return project.ahead.vault + project.ahead.platform
}

function pendingCommits(project: ProjectDto): number {
  return project.commits.length
}

function openTasks(project: ProjectDto): number {
  return project.tasks.filter((task) => task.st !== 'x').length
}

function normalizeProjectId(value: string | null | undefined): string {
  return (value ?? '').replace(/^\/+|\/+$/g, '')
}

function isProjectOrChild(parentId: string, candidateId: string | null | undefined): boolean {
  const parent = normalizeProjectId(parentId)
  const candidate = normalizeProjectId(candidateId)
  return Boolean(parent && candidate && (candidate === parent || candidate.startsWith(`${parent}/`)))
}

function isActiveSession(session: OwnerSessionDto): boolean {
  return session.status === 'running' || session.status === 'idle'
}

function projectSessions(project: ProjectDto, sessions: OwnerSessionDto[]): OwnerSessionDto[] {
  return sessions.filter((session) => isActiveSession(session) && isProjectOrChild(project.id, session.project))
}

function updatedMs(project: ProjectDto): number {
  const ts = Date.parse(project.lastUpdate)
  return Number.isFinite(ts) ? ts : 0
}

function formatUpdated(value: string): string {
  const ts = Date.parse(value)
  if (!Number.isFinite(ts)) return value || 'unknown'
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(ts))
}

function matches(project: ProjectDto, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  return [
    project.id,
    project.name,
    project.desc,
    project.title,
    project.sprint?.id,
    project.sprint?.goal,
    project.chatAgent,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(needle)
}

function sortProjects(projects: ProjectDto[], sort: SortMode): ProjectDto[] {
  return [...projects].sort((a, b) => {
    if (sort === 'ahead') {
      return pendingCommits(b) - pendingCommits(a) || aheadCount(b) - aheadCount(a) || updatedMs(b) - updatedMs(a)
    }
    return updatedMs(b) - updatedMs(a) || a.id.localeCompare(b.id)
  })
}

function matchesFocus(project: ProjectDto, focus: FocusMode, sessions: OwnerSessionDto[]): boolean {
  if (focus === 'sessions') return projectSessions(project, sessions).length > 0
  if (focus === 'ahead') return pendingCommits(project) > 0 || aheadCount(project) > 0
  if (focus === 'tasks') return openTasks(project) > 0
  return true
}

function Tile({ project, size = 42 }: { project: ProjectDto; size?: number }) {
  return (
    <span
      aria-hidden
      style={{
        alignItems: 'center',
        background: projectGradient(project),
        borderRadius: Math.round(size / 4),
        boxShadow: '0 10px 26px rgba(56, 189, 248, 0.16)',
        color: '#04111d',
        display: 'inline-flex',
        flexShrink: 0,
        fontSize: size > 44 ? 18 : 13,
        fontWeight: 800,
        height: size,
        justifyContent: 'center',
        width: size,
      }}
    >
      {project.abbr}
    </span>
  )
}

function AheadPill({ project }: { project: ProjectDto }) {
  const parts = [
    project.ahead.vault > 0 ? `vault +${project.ahead.vault}` : '',
    project.ahead.platform > 0 ? `платформа +${project.ahead.platform}` : '',
  ].filter(Boolean)
  if (parts.length === 0 && project.commits.length === 0) {
    return <Pill tone="success">synced</Pill>
  }
  return <Pill tone="info">{parts.length > 0 ? parts.join(' · ') : `ahead +${project.commits.length}`}</Pill>
}

function ProjectCard({ project }: { project: ProjectDto }) {
  const sprint = project.sprint
  const status = STATUS_META[project.status]

  return (
    <Link
      className="projects-card"
      to={projectPath(project.id)}
      style={{
        background: project.status === 'closed' ? 'var(--frm-workbench-well-2)' : 'var(--frm-workbench-well)',
        border: `1px solid ${project.status === 'active' ? 'rgba(56, 189, 248, 0.22)' : 'var(--frm-line-subtle)'}`,
        borderRadius: 15,
        color: 'inherit',
        display: 'flex',
        flexDirection: 'column',
        gap: 13,
        minHeight: 190,
        opacity: project.status === 'closed' ? 0.82 : 1,
        padding: '17px 18px',
        textDecoration: 'none',
        transition: 'border-color 150ms ease, transform 150ms ease',
      }}
    >
      <div style={{ alignItems: 'flex-start', display: 'flex', gap: 13, minWidth: 0 }}>
        <Tile project={project} />
        <div style={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-primary)', fontSize: 14.5, fontWeight: 700 }}>
              {project.name}
            </span>
            <StatusPill status={status.statusKind} label={status.label} />
          </div>
          <span style={{ color: 'var(--frm-text-muted)', fontSize: 13, lineHeight: 1.45 }}>
            {project.desc || project.title}
          </span>
        </div>
        <ChevronIcon />
      </div>

      <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {sprint ? (
          <Pill tone="info" leftIcon={<BoltIcon />}>
            <span style={MONO_NUMERIC_STYLE}>{sprint.id}</span>
            <span style={{ color: 'var(--frm-text-muted)', marginLeft: 6 }}>{sprint.done}/{sprint.total}</span>
          </Pill>
        ) : (
          <Pill tone="neutral">{project.status === 'planned' ? 'спринт не открыт' : 'архив'}</Pill>
        )}
        <span style={{ flex: 1 }} />
        {(aheadCount(project) > 0 || project.commits.length > 0) ? <AheadPill project={project} /> : null}
      </div>

      <div
        style={{
          alignItems: 'center',
          borderTop: '1px solid var(--frm-line-faint)',
          display: 'flex',
          gap: 10,
          marginTop: 'auto',
          paddingTop: 11,
        }}
      >
        <span style={{ ...MUTED_STYLE, alignItems: 'center', display: 'inline-flex', gap: 6, fontSize: 11.5 }}>
          <ClockIcon />
          обновлён {formatUpdated(project.lastUpdate)}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ ...MONO_NUMERIC_STYLE, alignItems: 'center', color: projectAccent(project), display: 'inline-flex', fontSize: 11, gap: 6 }}>
          <span style={{ background: projectAccent(project), borderRadius: '50%', height: 5, width: 5 }} />
          {project.chatAgent}
        </span>
      </div>
    </Link>
  )
}

function ProjectGroups({ projects }: { projects: ProjectDto[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {STATUS_ORDER.map((status) => {
        const groupProjects = projects.filter((project) => project.status === status)
        if (groupProjects.length === 0) return null
        const meta = STATUS_META[status]
        const pending = groupProjects.reduce((sum, project) => sum + pendingCommits(project), 0)
        return (
          <section key={status} style={STACK_STYLE}>
            <div style={{ alignItems: 'center', display: 'flex', gap: 11, padding: '0 2px' }}>
              <span style={{ background: meta.dot, borderRadius: '50%', flexShrink: 0, height: 9, width: 9 }} />
              <span style={{ color: 'var(--frm-text-primary)', fontSize: 14, fontWeight: 800 }}>{meta.groupLabel}</span>
              <span style={{ ...MUTED_STYLE, fontSize: 12 }}>{meta.note}</span>
              <span style={{ flex: 1 }} />
              <Pill tone={meta.tone}>
                {groupProjects.length} проект(ов){status === 'active' && pending > 0 ? ` · +${pending} к выпуску` : ''}
              </Pill>
            </div>
            <div className="projects-card-grid" style={CARD_GRID_STYLE}>
              {groupProjects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}

function ProjectTree({ projects, sessions }: { projects: ProjectDto[]; sessions: OwnerSessionDto[] }) {
  const nodes = useMemo(() => buildProjectTreeNodes(projects, sessions), [projects, sessions])
  const expandedIds = useMemo(() => collectNodeIds(nodes), [nodes])

  return (
    <div style={STACK_STYLE}>
      <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 12, padding: '0 2px' }}>
        <span style={{ ...MONO_NUMERIC_STYLE, alignItems: 'center', color: 'var(--frm-text-muted)', display: 'inline-flex', fontSize: 12.5, gap: 8 }}>
          <FolderIcon color="var(--frm-accent-2)" />
          vault-new · дерево проектов как /project
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ ...MUTED_STYLE, alignItems: 'center', display: 'inline-flex', fontSize: 11.5, gap: 7 }}>
          <Pill tone="info" size="sm">сразу раскрыто</Pill>
          вложенные подпроекты без служебных папок
        </span>
      </div>
      <FileTree
        nodes={nodes}
        depth={8}
        expandedIds={expandedIds}
        renderBadge={(node) => node.badge}
        renderActions={(node) => {
          const project = (node as ProjectTreeNode).project
          return project ? (
            <Link
              to={projectPath(project.id)}
              style={{
                alignItems: 'center',
                background: 'var(--frm-workbench-panel-2)',
                border: '1px solid rgba(56, 189, 248, 0.28)',
                borderRadius: 7,
                color: 'var(--frm-accent)',
                display: 'inline-flex',
                flexShrink: 0,
                fontSize: 11,
                fontWeight: 800,
                gap: 5,
                height: 26,
                padding: '0 10px',
                textDecoration: 'none',
              }}
            >
              Карточка
              <ChevronIcon />
            </Link>
          ) : null
        }}
      />
    </div>
  )
}

type ProjectTreeNode = FileTreeNode & {
  project?: ProjectDto
}

interface ProjectHierarchyNode {
  id: string
  segment: string
  project?: ProjectDto
  children: Map<string, ProjectHierarchyNode>
}

function buildProjectTreeNodes(projects: ProjectDto[], sessions: OwnerSessionDto[]): ProjectTreeNode[] {
  const root: ProjectHierarchyNode = {
    id: 'vault-new',
    segment: 'vault-new',
    children: new Map(),
  }

  projects.forEach((project) => {
    const parts = normalizeProjectId(project.id).split('/').filter(Boolean)
    let cursor = root
    parts.forEach((part, index) => {
      const path = parts.slice(0, index + 1).join('/')
      let next = cursor.children.get(path)
      if (!next) {
        next = {
          id: path,
          segment: part,
          children: new Map(),
        }
        cursor.children.set(path, next)
      }
      if (index === parts.length - 1) {
        next.project = project
      }
      cursor = next
    })
  })

  return [{
    id: 'vault:default',
    name: 'vault-new/',
    kind: 'dir',
    tone: 'accent',
    badge: `${projects.length} project nodes`,
    children: Array.from(root.children.values()).map((node) => hierarchyTreeNode(node, sessions)),
  }]
}

function hierarchyTreeNode(node: ProjectHierarchyNode, sessions: OwnerSessionDto[]): ProjectTreeNode {
  const children = Array.from(node.children.values()).map((child) => hierarchyTreeNode(child, sessions))
  const descendantCount = countProjects(node)
  if (!node.project) {
    return {
      id: `group:${node.id}`,
      name: `${node.segment}/`,
      kind: 'dir',
      tone: 'neutral',
      badge: `${descendantCount} подпроект(ов)`,
      meta: 'group',
      children,
    }
  }

  return {
    ...projectTreeNode(node.project, sessions),
    children,
  }
}

function countProjects(node: ProjectHierarchyNode): number {
  let count = node.project ? 1 : 0
  node.children.forEach((child) => {
    count += countProjects(child)
  })
  return count
}

function collectNodeIds(nodes: ProjectTreeNode[]): string[] {
  return nodes.flatMap((node) => [
    ...(node.id ? [node.id] : []),
    ...collectNodeIds((node.children ?? []) as ProjectTreeNode[]),
  ])
}

function projectTreeNode(project: ProjectDto, sessions: OwnerSessionDto[]): ProjectTreeNode {
  const pending = pendingCommits(project)
  const activeSessions = projectSessions(project, sessions).length
  const badge = activeSessions > 0
    ? `${activeSessions} сесс.`
    : pending > 0
      ? `ahead +${pending}`
      : project.status === 'active'
        ? 'synced'
        : project.status
  return {
    id: `project:${project.id}`,
    name: `${project.id}/`,
    kind: 'dir',
    tone: pending > 0 ? 'info' : project.status === 'planned' ? 'warning' : project.status === 'active' ? 'success' : 'neutral',
    badge,
    meta: project.sprint ? `${project.sprint.id} · ${formatUpdated(project.lastUpdate)}` : formatUpdated(project.lastUpdate),
    project,
    children: [],
  }
}

function ProjectsSkeleton() {
  return (
    <div style={STACK_STYLE}>
      <Skeleton height={128} />
      <Skeleton height={360} />
    </div>
  )
}

function focusFromParam(value: string | null): FocusMode {
  if (value === 'sessions' || value === 'ahead' || value === 'tasks') return value
  if (value === 'unreleased') return 'ahead'
  return 'all'
}

export function ProjectsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [view, setView] = useState<ViewMode>('cards')
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState<ProjectStatus | 'all'>('all')
  const [focus, setFocus] = useState<FocusMode>(() => focusFromParam(searchParams.get('focus')))
  const [sort, setSort] = useState<SortMode>('updated')

  const projectsQuery = useQuery({
    queryKey: ['projects'],
    queryFn: fetchProjects,
    refetchInterval: 60_000,
  })
  const sessionsQuery = useQuery({
    queryKey: ['sessions', 'projects-summary'],
    queryFn: fetchSessions,
    refetchInterval: 30_000,
  })

  const projects = projectsQuery.data ?? []
  const sessions = sessionsQuery.data ?? []
  useEffect(() => {
    const nextFocus = focusFromParam(searchParams.get('focus'))
    setFocus((current) => (current === nextFocus ? current : nextFocus))
  }, [searchParams])

  const handleFocusChange = useCallback((nextFocus: FocusMode) => {
    setFocus(nextFocus)
    const next = new URLSearchParams(searchParams)
    if (nextFocus === 'all') {
      next.delete('focus')
    } else {
      next.set('focus', nextFocus)
    }
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const visible = useMemo(() => {
    const scoped = projects.filter((project) => {
      if (status !== 'all' && project.status !== status) return false
      if (!matchesFocus(project, focus, sessions)) return false
      return matches(project, query)
    })
    return sortProjects(scoped, sort)
  }, [focus, projects, query, sessions, sort, status])

  const summary = useMemo(() => ({
    active: projects.filter((project) => project.status === 'active').length,
    ahead: projects.reduce((sum, project) => sum + pendingCommits(project), 0),
    planned: projects.filter((project) => project.status === 'planned').length,
    tasks: projects.reduce((sum, project) => sum + openTasks(project), 0),
  }), [projects])

  return (
    <div style={PAGE_STYLE}>
      <WorkbenchPanel style={PANEL_STYLE}>
        <PageHeader
          dense
          eyebrow="Раздел · Проекты"
          title="Проекты"
          description={`${formatInteger(projects.length)} проектов экосистемы · context.md + roadmap активного спринта · обновлено ${projectsQuery.dataUpdatedAt ? formatUpdated(new Date(projectsQuery.dataUpdatedAt).toISOString()) : '...'}`}
          actions={
            <Button
              variant="secondary"
              size="md"
              loading={projectsQuery.isFetching}
              leftIcon={<FrmIcon name="refresh" size={15} />}
              onClick={() => void projectsQuery.refetch()}
            >
              Синк context.md
            </Button>
          }
        />
        <MetricStrip
          minColumnWidth={150}
          density="compact"
          cardLayout="row"
          style={{ marginTop: 20 }}
          items={[
            { id: 'active', label: 'активных проектов', value: formatInteger(summary.active), tone: 'success' },
            { id: 'ahead', label: 'невыпущенных коммитов', value: formatInteger(summary.ahead), tone: summary.ahead > 0 ? 'warning' : 'neutral' },
            { id: 'planned', label: 'в планировании', value: formatInteger(summary.planned), tone: 'info' },
            { id: 'tasks', label: 'открытых задач спринтов', value: formatInteger(summary.tasks), tone: 'success' },
          ]}
        />
      </WorkbenchPanel>

      <WorkbenchPanel style={PANEL_STYLE}>
        <div className="projects-toolbar" style={TOOLBAR_STYLE}>
          <SearchInput
            aria-label="Поиск проектов"
            placeholder="Поиск по названию проекта"
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            style={{ flex: '1 1 300px', maxWidth: 440, minWidth: 260 }}
          />
          <div className="projects-toolbar-controls" style={{ alignItems: 'center', display: 'flex', flex: '0 0 auto', flexWrap: 'nowrap', gap: 10 }}>
            <SegmentedControl
              value={view}
              onChange={(id) => setView(id as ViewMode)}
              ariaLabel="Вид списка проектов"
              items={[
                { id: 'cards', label: 'Карточки' },
                { id: 'tree', label: 'Дерево' },
              ]}
            />
            <Select
              aria-label="Фильтр фокуса проектов"
              controlSize="sm"
              value={focus}
              onChange={(event) => handleFocusChange(event.currentTarget.value as FocusMode)}
              options={[
                { value: 'all', label: 'Все фокусы' },
                { value: 'sessions', label: 'С активными сессиями' },
                { value: 'ahead', label: 'Невыпущено' },
                { value: 'tasks', label: 'Открытые задачи' },
              ]}
            />
            <Select
              aria-label="Фильтр статуса проекта"
              controlSize="sm"
              value={status}
              onChange={(event) => setStatus(event.currentTarget.value as ProjectStatus | 'all')}
              options={[
                { value: 'all', label: 'Все' },
                { value: 'active', label: 'Активные' },
                { value: 'planned', label: 'В планах' },
                { value: 'closed', label: 'Закрытые' },
              ]}
            />
            <Select
              aria-label="Сортировка проектов"
              controlSize="sm"
              value={sort}
              onChange={(event) => setSort(event.currentTarget.value as SortMode)}
              options={[
                { value: 'updated', label: 'По обновлению' },
                { value: 'ahead', label: 'По невыпущенному' },
              ]}
            />
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          {projectsQuery.isLoading ? (
            <ProjectsSkeleton />
          ) : projectsQuery.isError ? (
            <ErrorBanner
              title="Не удалось загрузить проекты"
              description={projectsQuery.error instanceof Error ? projectsQuery.error.message : 'unknown error'}
              action={<Button onClick={() => void projectsQuery.refetch()}>Повторить</Button>}
            />
          ) : visible.length === 0 ? (
            <EmptyState
              title="Ничего не найдено по текущим фильтрам"
              description="Сбрось поиск или фильтр статуса."
              action={<Button onClick={() => { setQuery(''); setStatus('all') }}>Сбросить фильтры</Button>}
            />
          ) : view === 'cards' ? (
            <ProjectGroups projects={visible} />
          ) : (
            <ProjectTree projects={visible} sessions={sessions} />
          )}
        </div>
      </WorkbenchPanel>
    </div>
  )
}

function ChevronIcon({ open = false }: { open?: boolean }) {
  return <FrmIcon name="chevron" size={13} style={{ color: 'var(--frm-text-muted)', transform: open ? undefined : 'rotate(-90deg)', transition: 'transform 150ms ease' }} />
}

function BoltIcon() {
  return (
    <svg aria-hidden width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M13 2 3 14h7v8l10-12h-7z" />
    </svg>
  )
}

function ClockIcon() {
  return (
    <svg aria-hidden width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  )
}

function FolderIcon({ color }: { color: string }) {
  return (
    <svg aria-hidden width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" />
    </svg>
  )
}
